// Share a poll: ask the API for its short link (POST /polls/:id/share, which
// mints the slug on first use), then hand it to the Web Share API, falling back
// to the clipboard. Returns { url, channel } where channel is 'web_share',
// 'copy' or 'cancelled'; throws only when neither path is available.
import { postJson } from '../api';
import { trackShare } from './analytics';

export function pollShareUrl(poll) {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://votomoto.com';
  if (poll?.share_url) return poll.share_url;
  if (poll?.share_slug) return `${origin}/p/${poll.share_slug}`;
  return `${origin}/poll/${poll?.id}`;
}

export function displayUrl(url) {
  return String(url || '').replace(/^https?:\/\//, '');
}

export async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  return false;
}

async function resolveUrl(poll, channelHint) {
  try {
    const data = await postJson(`/polls/${poll.id}/share`, { channel: channelHint });
    if (data && typeof data.url === 'string' && data.url) return data.url;
    if (data && data.share_slug) return pollShareUrl({ ...poll, share_slug: data.share_slug });
  } catch (_) {
    // The share endpoint may not exist yet (older backend) — fall back below.
  }
  return pollShareUrl(poll);
}

export async function sharePoll(poll, { preferCopy = false, where = 'card' } = {}) {
  const canNative = !preferCopy && typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const url = await resolveUrl(poll, canNative ? 'web_share' : 'copy');
  if (canNative) {
    try {
      await navigator.share({ title: poll.caption || 'Vote on Voto', text: poll.caption || undefined, url });
      trackShare('web_share', { poll_id: poll.id, where });
      return { url, channel: 'web_share' };
    } catch (err) {
      if (err && err.name === 'AbortError') return { url, channel: 'cancelled' };
      // Share sheet unavailable (e.g. desktop without a target) — copy instead.
    }
  }
  if (await copyText(url)) {
    trackShare('copy', { poll_id: poll.id, where });
    return { url, channel: 'copy' };
  }
  throw new Error('share unavailable');
}
